import React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { Logger } from "../../../../lib/logger/Logger";
import { validateEmail } from "../../../../dentlab/src/hooks/useForm/form-field-validation-functions";
import axios from "axios";
import { Button } from "../../../../dentlab/src/components/ui/button";
import { Input } from "../../../../dentlab/src/components/ui/input";
import { Label } from "../../../../dentlab/src/components/ui/label";
import SecureIcon from "./secure.svg";
import { AuthBanner } from "../../auth-banner/auth-banner.component";

// TODO: If the user doesn't exist, signInWithOtp() will signup the user instead. To restrict this behaviour, you can set shouldCreateUser in SignInWithPasswordlessCredentials.options to false.
// --> either check yourself whether user exist or do config in supabase

export const DentistSignIn: React.FC<{
	onSignIn: (data: any) => void;
	fallbackUrl: string;
}> = ({ onSignIn, fallbackUrl }) => {
	const [searchParams, _] = useSearchParams();

	const [email, setEmail] = useState<string>("");
	const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
	const [otp, setOTP] = useState<string>("");
	const [loginMode, setLoginMode] = useState<'otp' | 'password'>('otp');
	const [password, setPassword] = useState<string>("");

	useEffect(() => {
		if (searchParams.has("email")) {
			setEmail(searchParams.get("email") as string);
		}
	}, [searchParams]);

	const handleSendOTP = async () => {
		Logger.log("DentistSignIn", "handleSendOTP", email);
		setLoginError(null);
		let data;
		let error;
		const { data: supabaseData, error: supabaseError } = await supabase.auth.signInWithOtp({
			email: email.toLowerCase(),
		});
		data = supabaseData;
		error = supabaseError;
		if (error) {
			try {
				const response = await axios({
					url: `${fallbackUrl}/login/request-otp`,
					method: "POST",
					data: {
						email: email.toLowerCase(),
					},
				});
				data = response.data.data;
				error = response.data.error;
				if (!error) {
					setIsOTPSent(true);
					return;
				} else {
					setLoginError({
						message: "Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
						isCritical: false
					});
				}
			} catch (error) {
				Logger.error(error, { identifier: "otp" }, { data, error, email });
				setLoginError({
					message: "Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
					isCritical: true
				});
			}
		} else {
			setIsOTPSent(true);
		}
		Logger.log("DentistSignIn", "handleSendOTP", data, error);
	};

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setOTP(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handleSubmitOtp = async () => {
		Logger.log("DentistSignIn", "handleSubmitOtp", email, otp);
		setLoginError(null);
		const token = otp;
		let data;
		let error;
		const { data: supabaseData, error: supabaseError } = await supabase.auth.verifyOtp({
			email,
			token,
			type: "email",
		});
		data = supabaseData;
		error = supabaseError;
		Logger.log("DentistSignIn", "handleSubmitOtp", data, error);
		if (error) {
			try {
				const response = await axios({
					url: `${fallbackUrl}/login/verify-otp`,
					method: "POST",
					data: {
						email: email.toLowerCase(),
						token: token,
					},
				});
				data = response.data.data;
				error = response.data.error;
				if (error) {
					setLoginError({
						message: "Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
						isCritical: false
					});
				} else {
					onSignIn(data);
				}
			} catch (error) {
				Logger.error(error, { identifier: "otp" }, { email, otp });
				setLoginError({
					message: "Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
					isCritical: true
				});
			}
		} else {
			onSignIn(data);
		}
	};

	const handlePasswordLogin = async () => {
		Logger.log("DentistSignIn", "handlePasswordLogin", email);
		setLoginError(null);
		
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email: email.toLowerCase(),
				password: password,
			});

			if (error) {
				setLoginError({
					message: "Falsches Passwort oder E-Mail. Nutzen Sie alternativ das Einmalpasswort.",
					isCritical: false
				});
			} else if (data) {
				onSignIn(data);
			}
		} catch (error) {
			Logger.error(error, { identifier: "password-login" }, { email });
			setLoginError({
				message: "Fehler beim Einloggen. Bitte versuchen Sie es erneut.",
				isCritical: true
			});
		}
	};

	const [error, setError] = useState(false);
	const [loginError, setLoginError] = useState<{ message: string; isCritical?: boolean } | null>(null);

	const handleBlur = (e: any) => {
		const email = e.target.value;
		setError(!validateEmail(email));
	};

	return (
		<div className="flex min-h-screen flex-col">
			<AuthBanner />
			<div className="flex flex-1">
				{/* Left Section - OTP Form */}
			<div className="w-full max-w-md lg:max-w-lg bg-gray-50 p-8 lg:p-12 flex items-center justify-center border-r border-gray-200 shadow-xl mr-8">
				<div className="w-full max-w-sm space-y-6">
					<div className="space-y-2">
						<img src={SecureIcon} alt="Secure" className="w-14 h-14 mb-4 -mt-12" />
						<h1 className="text-3xl font-bold tracking-tight text-gray-900">
							{loginMode === 'otp' ? 'Sicherer Zugriff mit Einmalpasswort' : 'Anmelden mit Passwort'}
						</h1>
						<p className="text-gray-600">
							{loginMode === 'otp'
								? 'Geben Sie Ihre E-Mail ein, um ein Einmalpasswort zu erhalten. Nach dem Login bleiben Sie auf diesem Gerät angemeldet.'
								: 'Melden Sie sich mit Ihrer E-Mail und Ihrem Passwort an.'}
						</p>
					</div>

					<div className="space-y-4">
						{loginMode === 'otp' ? (
							// OTP Mode
							isOTPSent ? (
								<>
									<div className="space-y-2">
										<Label htmlFor="otp">Zugangscode</Label>
										<Input
											id="otp"
											type="text"
											placeholder="Geben Sie Ihren Code ein"
											value={otp || ""}
											onChange={handleOtpChange}
											className="h-12"
										/>
									</div>
									{loginError && (
										<div className="rounded-md bg-red-50 p-4">
											<p className="text-sm text-red-800">{loginError.message}</p>
											{loginError.isCritical && (
												<p className="text-sm text-red-800 mt-2">
													Für Unterstützung kontaktieren Sie uns unter: <strong>+41 31 561 93 27</strong>
												</p>
											)}
										</div>
									)}
									<Button
										onClick={handleSubmitOtp}
										size="lg"
										className="w-full h-12 text-base"
									>
										Eingabe
									</Button>
								</>
							) : (
								<>
									<div className="space-y-2">
										<Input
											id="email"
											type="email"
											placeholder="E-Mail Adresse"
											value={email ?? ""}
											onChange={handleEmailChange}
											onBlur={handleBlur}
											className={`h-12 ${error ? "border-red-500" : ""}`}
											required
										/>
										{error && (
											<p className="text-sm text-red-500">Format stimmt nicht</p>
										)}
									</div>
									{loginError && (
										<div className="rounded-md bg-red-50 p-4">
											<p className="text-sm text-red-800">{loginError.message}</p>
											{loginError.isCritical && (
												<p className="text-sm text-red-800 mt-2">
													Für Unterstützung kontaktieren Sie uns unter: <strong>+41 31 561 93 27</strong>
												</p>
											)}
										</div>
									)}
									<Button
										onClick={handleSendOTP}
										size="lg"
										className="w-full h-12 text-base"
									>
										Zugangscode senden
									</Button>
								</>
							)
						) : (
							// Password Mode
							<>
								<div className="space-y-2">
									<Input
										id="email"
										type="email"
										placeholder="E-Mail Adresse"
										data-testid="connect-login-email-input"
										aria-label="E-Mail Adresse"
										value={email ?? ""}
										onChange={handleEmailChange}
										onBlur={handleBlur}
										className={`h-12 ${error ? "border-red-500" : ""}`}
										required
									/>
									{error && (
										<p className="text-sm text-red-500">Format stimmt nicht</p>
									)}
								</div>
								<div className="space-y-2">
									<Input
										id="password"
										type="password"
										placeholder="Passwort"
										data-testid="connect-login-password-input"
										aria-label="Passwort"
										value={password}
										onChange={handlePasswordChange}
										className="h-12"
										required
									/>
								</div>
								{loginError && (
									<div className="rounded-md bg-red-50 p-4">
										<p className="text-sm text-red-800">{loginError.message}</p>
										{loginError.isCritical && (
											<p className="text-sm text-red-800 mt-2">
												Für Unterstützung kontaktieren Sie uns unter: <strong>+41 31 561 93 27</strong>
											</p>
										)}
									</div>
								)}
								<Button
									onClick={handlePasswordLogin}
									size="lg"
									className="w-full h-12 text-base"
									data-testid="connect-login-submit-button"
									aria-label="Anmelden"
								>
									Anmelden
								</Button>
							</>
						)}
					</div>

					{import.meta.env.DEV && (
					<div className="flex flex-wrap gap-2" data-testid="dev-autofill">
						<button type="button" onClick={async () => {
							const { data, error } = await supabase.auth.signInWithPassword({ email: 'connect@dentdesk.ch', password: 'Test1234!' });
							if (data && !error) onSignIn(data);
						}} className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200">Zahnarzt (Thomas)</button>
						<button type="button" onClick={async () => {
							const { data, error } = await supabase.auth.signInWithPassword({ email: 'assistant@dentdesk.ch', password: 'Test1234!' });
							if (data && !error) onSignIn(data);
						}} className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200">Assistenz (Sandra)</button>
					</div>
				)}

				{/* Mode Switch Link */}
					<div className="text-center">
						{loginMode === 'otp' && !isOTPSent ? (
							<button
								type="button"
								onClick={() => setLoginMode('password')}
								className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
								data-testid="connect-login-password-mode-toggle"
							>
								Schon registriert? Zum Passwort-Login
							</button>
						) : loginMode === 'password' ? (
							<button
								type="button"
								onClick={() => {
									setLoginMode('otp');
									setPassword('');
									setLoginError(null);
								}}
								className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
							>
								Zurück zum Einmalpasswort-Login
							</button>
						) : null}
					</div>
				</div>
			</div>

			{/* Right Section - Value Propositions */}
			<div className="flex-1 bg-white flex items-center justify-center p-8 lg:p-12">
				<div className="max-w-lg space-y-8">
					<div className="space-y-4">
						<h2 className="text-4xl font-bold tracking-tight text-gray-900">
							Alle Aufträge an einem Ort
						</h2>
						<p className="text-lg text-gray-600">
							dentCONNECT ist Ihre sichere Plattform für die
							Zusammenarbeit mit ihrem Labor -{" "}
							<b>so einfach wie ein Online-Shop</b>.
						</p>
					</div>
					<div className="space-y-4">
						<div className="flex items-start space-x-3">
							<svg
								className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<p className="text-gray-700">
								<b>Sicherer Zugriff</b> auf alle Lieferscheine und
								Kostenvoranschläge
							</p>
						</div>

						<div className="flex items-start space-x-3">
							<svg
								className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<p className="text-gray-700">
								<b>Direkte Kommunikation</b> und sicherer
								Dateiaustausch zu jedem Auftrag
							</p>
						</div>

						<div className="flex items-start space-x-3">
							<svg
								className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<p className="text-gray-700">
								<b>Digitale Auftragserfassung</b> über individuell
								bereitgestellte Formulare
							</p>
						</div>

						<div className="flex items-start space-x-3">
							<svg
								className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<p className="text-gray-700">
								<b>Einfache Übersicht</b> und <b>schneller Zugriff</b> auf
								Rechnungen
							</p>
						</div>
						<p className="text-md pt-4 text-slate-700">
							Jetzt <a href="https://calendly.com/simon-hans-dentdesk/30-minuten-schulung-feedback-gesprach" target="_blank" rel="noopener noreferrer" className="text-slate-700 underline hover:text-blue-600">
								kostenlose Online-Schulung
							</a> für Sie oder Ihr Team buchen.
						</p>
					</div>
				</div>
			</div>
			</div>
		</div>
	);
};
